import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Upload, FileCheck, FileX, Clock } from "lucide-react";

const STATUS_META = {
  under_review: { icon: Clock, color: "var(--gold)", label: "Under review" },
  active: { icon: FileCheck, color: "var(--mint)", label: "Published" },
  rejected: { icon: FileX, color: "var(--rust)", label: "Rejected" },
  expired: { icon: Clock, color: "var(--ink-muted)", label: "Expired" },
  recalled: { icon: FileX, color: "var(--rust)", label: "Recalled" },
};

export default function AccountCOAs() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/coas/mine").then((r) => { setItems(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api.get("/products").then((r) => setProducts(r.data || [])).catch(() => {});
  }, []);

  return (
    <div data-testid="account-coas">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-display text-2xl tracking-tight">My COA submissions</h2>
        <button onClick={() => setSubmitting(true)} className="btn-primary inline-flex items-center gap-2" data-testid="submit-coa-btn">
          <Upload className="h-4 w-4" /> Submit a COA
        </button>
      </div>
      <p className="text-sm text-[var(--ink-muted)] mb-6">
        Independently tested a batch you bought from us? Upload the lab's PDF report and we'll review + publish it to the COA Hub so the whole community benefits.
      </p>

      {loading && <p className="text-sm text-[var(--ink-muted)]">Loading…</p>}
      {!loading && items.length === 0 && (
        <div className="border border-dashed border-[var(--line)] rounded-md p-8 text-center text-sm text-[var(--ink-muted)]">
          No submissions yet. Click <b>Submit a COA</b> to upload your first one.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((c) => {
          const meta = STATUS_META[c.status] || STATUS_META.under_review;
          const Icon = meta.icon;
          return (
            <div key={c.id} className="border border-[var(--line)] rounded-md p-5 bg-[var(--paper-2)]" data-testid={`my-coa-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-[var(--terra)]">{c.product_sku}</div>
                  <div className="font-display text-lg mt-1">Batch {c.batch_number}</div>
                  <div className="text-xs text-[var(--ink-muted)] font-mono mt-1">{c.testing_lab} · {c.test_date}</div>
                </div>
                <span className="inline-flex items-center gap-1.5 font-mono text-[10.5px] tracking-[0.16em] uppercase whitespace-nowrap" style={{ color: meta.color }}>
                  <Icon className="h-3.5 w-3.5" /> {meta.label}
                </span>
              </div>
              {c.purity_percent && (
                <div className="mt-3 font-mono text-sm">Purity: <b>{c.purity_percent}%</b></div>
              )}
              {c.review_notes && (
                <div className="mt-3 text-xs border-l-2 border-[var(--line)] pl-3 text-[var(--ink-soft)]">
                  <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--ink-muted)] mb-1">Admin notes</div>
                  {c.review_notes}
                </div>
              )}
              {c.coa_pdf_url && (
                <a href={c.coa_pdf_url} target="_blank" rel="noreferrer" className="mt-3 inline-block text-xs text-[var(--terra)] hover:underline font-mono">View PDF →</a>
              )}
            </div>
          );
        })}
      </div>

      {submitting && (
        <SubmitModal
          products={products}
          onClose={() => setSubmitting(false)}
          onSubmitted={() => { setSubmitting(false); load(); }}
        />
      )}
    </div>
  );
}

function SubmitModal({ products, onClose, onSubmitted }) {
  const [f, setF] = useState({
    product_sku: products[0]?.sku || "",
    batch_number: "",
    testing_lab: "",
    test_date: new Date().toISOString().slice(0, 10),
    purity_percent: "",
    testing_methods: "HPLC, MS",
    notes: "",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const onF = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    if (!file) { toast.error("Please attach a PDF"); return; }
    if (file.type !== "application/pdf") { toast.error("Only PDF files are accepted"); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("PDF must be under 15 MB"); return; }
    if (!f.product_sku || !f.batch_number || !f.testing_lab || !f.test_date) { toast.error("Product, batch #, lab, and test date are required"); return; }
    setBusy(true);
    try {
      const form = new FormData();
      Object.entries(f).forEach(([k, v]) => form.append(k, v));
      form.append("file", file);
      await api.post("/coas/submit", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Submitted for review. Admin will be in touch.");
      onSubmitted();
    } catch (e) { toast.error(e?.response?.data?.detail || "Submission failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm grid place-items-center z-50 p-4" data-testid="submit-coa-modal">
      <div className="bg-[var(--paper)] border border-[var(--line)] rounded-md w-full max-w-xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="eyebrow">Submit a COA</div>
        <h3 className="font-display text-xl mt-1 mb-2">Upload third-party lab report</h3>
        <p className="text-xs text-[var(--ink-muted)] mb-4">
          Submissions are reviewed by our compliance team within 1–2 business days. Approved COAs are published to the public COA Hub with credit to your submission email.
        </p>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <label className="col-span-2 text-xs">
            <span className="block text-[10.5px] tracking-[0.18em] uppercase text-[var(--ink-muted)] mb-1 font-mono">Product *</span>
            <select value={f.product_sku} onChange={onF("product_sku")} className="input w-full" data-testid="submit-coa-product">
              {products.map((p) => <option key={p.sku} value={p.sku}>{p.sku} — {p.product_name}</option>)}
            </select>
          </label>
          <Field label="Batch number *" v={f.batch_number} onC={onF("batch_number")} testid="submit-coa-batch" placeholder="e.g. NA26020X" />
          <Field label="Test date *" v={f.test_date} onC={onF("test_date")} testid="submit-coa-date" type="date" />
          <Field label="Testing lab *" v={f.testing_lab} onC={onF("testing_lab")} testid="submit-coa-lab" placeholder="e.g. Koveralabs / Janoshik / your university" full />
          <Field label="Purity %" v={f.purity_percent} onC={onF("purity_percent")} testid="submit-coa-purity" placeholder="99.42" />
          <Field label="Methods" v={f.testing_methods} onC={onF("testing_methods")} testid="submit-coa-methods" placeholder="HPLC, MS" />
          <label className="col-span-2 text-xs">
            <span className="block text-[10.5px] tracking-[0.18em] uppercase text-[var(--ink-muted)] mb-1 font-mono">Notes</span>
            <textarea value={f.notes} onChange={onF("notes")} className="input w-full" rows={2} placeholder="Anything we should know?" data-testid="submit-coa-notes" />
          </label>
          <label className="col-span-2 text-xs">
            <span className="block text-[10.5px] tracking-[0.18em] uppercase text-[var(--ink-muted)] mb-1 font-mono">PDF report * (max 15 MB)</span>
            <input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-xs" data-testid="submit-coa-file" />
            {file && <div className="mt-1 text-[11px] text-[var(--ink-muted)]">Selected: {file.name} ({Math.round(file.size / 1024)} KB)</div>}
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="btn-secondary" data-testid="submit-coa-cancel">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="submit-coa-send">
            <Upload className="h-4 w-4" /> {busy ? "Sending…" : "Send for review"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, v, onC, testid, type, full, placeholder }) {
  return (
    <label className={`text-xs ${full ? "col-span-2" : ""}`}>
      <span className="block text-[10.5px] tracking-[0.18em] uppercase text-[var(--ink-muted)] mb-1 font-mono">{label}</span>
      <input type={type || "text"} value={v} onChange={onC} placeholder={placeholder} className="input w-full" data-testid={testid} />
    </label>
  );
}
